import { memo, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { SUBMISSION_STATUSES } from '@didaskill/domain/assignments/constants';
import { OVERALL_AVERAGE_GRADING_SCALE } from '@didaskill/domain/courses/constants';
import { getTranscriptTeachingUnitAssignmentsCount } from '@didaskill/domain/students/utils';
import type { StudentAcademicTranscript } from '@didaskill/domain/users/types';
import { Typography } from '@didaskill/ui/design-system';
import { globalMessages, gradeMessages } from '@didaskill/ui/intl';

import { AcademicTranscriptAssignmentItem } from '../assignment-item/AcademicTranscriptAssignmentItem';
import * as styles from './AcademicTranscriptTeachingUnit.styles';

/**
 * Renders the `<Grade>` rich-text chunk of the teaching unit average message.
 * @param chunks - Content wrapped by the `<Grade>` tag.
 * @returns The emphasized grade.
 */
const renderGrade = (chunks: ReactNode[]): ReactNode => (
  <Typography variant={Typography.VARIANTS.HEADING_4} element="span">
    {chunks}
  </Typography>
);

/**
 * Renders the `<GradingScale>` rich-text chunk as-is.
 * @param chunks - Content wrapped by the `<GradingScale>` tag.
 * @returns The unchanged chunks.
 */
const renderGradingScale = (chunks: ReactNode[]): ReactNode => chunks;

export type AcademicTranscriptTeachingUnitProps = {
  teachingUnit: StudentAcademicTranscript['course']['teachingUnits'][0];
};

export const AcademicTranscriptTeachingUnitBase = ({
  teachingUnit,
}: AcademicTranscriptTeachingUnitProps) => (
  <div>
    <div css={styles.teachingUnitHeader}>
      <div>
        <Typography variant={Typography.VARIANTS.HEADING_4} element="span">
          {teachingUnit.code} - {teachingUnit.title}
        </Typography>

        <Typography variant={Typography.VARIANTS.HEADING_5} element="span" css={styles.coefficient}>
          <FormattedMessage
            {...(Number.isFinite(teachingUnit.overallAverage)
              ? gradeMessages.GRADE
              : globalMessages.NOT_AFFECTED)}
            values={{
              grade: teachingUnit.overallAverage,
              gradingScale: OVERALL_AVERAGE_GRADING_SCALE,
              Grade: renderGrade,
              GradingScale: renderGradingScale,
            }}
          />
        </Typography>
      </div>

      <div>
        <Typography variant={Typography.VARIANTS.BODY_MEDIUM_ITALIC} css={styles.coefficient}>
          <FormattedMessage
            {...gradeMessages.COEFFICIENT}
            values={{ coefficient: teachingUnit.coefficient }}
          />
        </Typography>

        <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR}>
          <FormattedMessage
            {...gradeMessages.GRADED_ASSIGNMENTS_COUNT}
            values={{
              ...getTranscriptTeachingUnitAssignmentsCount(teachingUnit, [
                SUBMISSION_STATUSES.CORRECTED,
              ]),
              b: (chunks) => (
                <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>{chunks}</Typography>
              ),
            }}
          />
        </Typography>
      </div>
    </div>

    {teachingUnit.assignments?.length > 0 && (
      <ul css={styles.assignmentsList} className="examList">
        {teachingUnit.assignments.map((transcriptAssignment) => (
          <AcademicTranscriptAssignmentItem
            key={transcriptAssignment.assignment.code}
            assignment={transcriptAssignment}
          />
        ))}
      </ul>
    )}

    {teachingUnit.lessons?.length > 0 && (
      <ul css={styles.lessonsList}>
        {teachingUnit.lessons.map((teachingUnitLesson) => (
          <li key={teachingUnitLesson.lesson.code}>
            <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD} css={styles.lessonTitle}>
              {teachingUnitLesson.lesson.title}
            </Typography>

            <ul css={styles.assignmentsList}>
              {teachingUnitLesson.assignments?.map((transcriptAssignment) => (
                <AcademicTranscriptAssignmentItem
                  key={transcriptAssignment.assignment.code}
                  assignment={transcriptAssignment}
                />
              ))}
            </ul>
          </li>
        ))}
      </ul>
    )}
  </div>
);

AcademicTranscriptTeachingUnitBase.displayName = 'AcademicTranscriptTeachingUnit';

export const AcademicTranscriptTeachingUnit = memo(AcademicTranscriptTeachingUnitBase);
